import { useState, useRef, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOSchema from '@/components/feature/SEOSchema';
import { allArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
    setOpenFaq(null);
  }, [slug]);

  const article = allArticles.find(a => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex items-center justify-center pt-32 pb-20">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900">Article Not Found</h1>
            <p className="mt-2 text-foreground-500 text-sm">The article you are looking for does not exist or has been moved.</p>
            <Link to="/" className="mt-5 inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium text-sm">
              <i className="ri-arrow-left-line"></i> Back to Home
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const renderContent = () => {
    return article.sections.map((section, i) => {
      switch (section.type) {
        case 'heading':
          if (section.level === 2) {
            return (
              <h2 key={i} id={`section-${i}`} className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-12 mb-5">
                {section.text}
              </h2>
            );
          }
          return (
            <h3 key={i} className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mt-8 mb-4">
              {section.text}
            </h3>
          );

        case 'paragraph':
          return (
            <p key={i} className="text-base md:text-lg text-foreground-700 leading-relaxed mb-5" dangerouslySetInnerHTML={{ __html: section.text }} />
          );

        case 'list':
          return (
            <ol key={i} className="list-decimal pl-5 md:pl-6 mb-5 space-y-3">
              {section.items!.map((item, j) => (
                <li key={j} className="text-base md:text-lg text-foreground-700 leading-relaxed pl-1" dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </ol>
          );

        case 'table':
          return (
            <div key={i} className="overflow-x-auto mb-6 rounded-xl border border-background-200/70">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-primary-500 text-background-50">
                    {section.headers!.map((h, j) => (
                      <th key={j} className={`py-3 px-4 text-left font-heading font-semibold ${j === 0 ? '' : 'text-center'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.rows!.map((row, j) => (
                    <tr key={j} className={j % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}>
                      {row.map((cell, k) => (
                        <td key={k} className={`py-3 px-4 ${k === 0 ? 'text-foreground-800 font-medium' : 'text-center text-foreground-600'}`}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );

        case 'faq':
          return (
            <div key={i} className="space-y-3 mb-6 mt-5">
              {section.items!.map((faq, j) => (
                <div key={j} className="rounded-xl border border-background-200/70 bg-background-50 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === j ? null : j)}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="text-sm md:text-base font-heading font-semibold text-foreground-900 pr-4">{faq.q}</span>
                    <span className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-all text-sm ${
                      openFaq === j ? 'bg-primary-500 text-background-50 rotate-45' : 'bg-background-100 text-foreground-500'
                    }`}>
                      <i className="ri-add-line"></i>
                    </span>
                  </button>
                  {openFaq === j && (
                    <div className="px-5 pb-5">
                      <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          );

        default:
          return null;
      }
    });
  };

  return (
    <div className="min-h-screen bg-background-50" ref={topRef}>
      <SEOSchema
        type="article"
        title={article.title}
        description={article.metaDescription}
        url={`https://kakobuy.boutique/articles/${article.slug}`}
        dateModified="2026-06-25"
        articleData={{
          author: article.author,
          datePublished: '2026-06-25',
          image: article.image,
        }}
      />
      <Navbar />

      {/* Article Hero */}
      <div className="relative bg-foreground-900 pt-24 pb-14 md:pt-28 md:pb-18">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-background-300 mb-6">
            <Link to="/" className="hover:text-background-50 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-background-400"></i>
            <span className="text-background-200">Blog</span>
            <i className="ri-arrow-right-s-line text-background-400"></i>
            <span className="text-background-100 font-medium truncate">{article.title}</span>
          </nav>

          <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50 leading-tight">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 mt-5">
            <div className="flex items-center gap-2 text-background-300 text-xs md:text-sm">
              <i className="ri-user-line"></i>
              <span>{article.author}</span>
            </div>
            <div className="flex items-center gap-2 text-background-300 text-xs md:text-sm">
              <i className="ri-calendar-line"></i>
              <span>Last Updated: {article.lastUpdated}</span>
            </div>
            <div className="flex items-center gap-2 text-background-300 text-xs md:text-sm">
              <i className="ri-time-line"></i>
              <span>{article.readingTime}</span>
            </div>
          </div>

          <div className="mt-5 p-4 rounded-xl bg-background-100/10 border border-background-200/20">
            <p className="text-xs md:text-sm text-background-300 leading-relaxed">
              <span className="text-background-200 font-semibold">Written by {article.author}</span> — {article.authorBio}
            </p>
          </div>
        </div>
      </div>

      <article className="max-w-[900px] mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Table of Contents */}
        <div className="mb-10 p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/60">
          <h4 className="text-sm font-heading font-bold text-foreground-900 uppercase tracking-wider mb-3">Table of Contents</h4>
          <ul className="space-y-2">
            {article.sections
              .filter(s => s.type === 'heading' && s.level === 2)
              .map((s, i) => {
                const sectionIndex = article.sections.indexOf(s);
                return (
                  <li key={i}>
                    <a href={`#section-${sectionIndex}`} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                      {s.text}
                    </a>
                  </li>
                );
              })}
          </ul>
        </div>

        {renderContent()}

        <div className="mt-10 p-5 md:p-6 rounded-xl bg-accent-50 border border-accent-200/60">
          <h4 className="text-sm font-heading font-bold text-foreground-900 mb-3">Explore Kakobuy Spreadsheet Categories</h4>
          <div className="flex flex-wrap gap-2">
            {article.categoryLinks.map((link, i) => (
              <a
                key={i}
                href={link.url}
                target="_blank"
                rel="noopener nofollow"
                className="px-3 py-1.5 rounded-full bg-background-50 text-xs md:text-sm font-medium text-foreground-700 border border-background-200/60 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                {link.name}
              </a>
            ))}
          </div>
        </div>

        <div className="mt-8 p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/60">
          <h4 className="text-sm font-heading font-bold text-foreground-900 mb-3">Related Articles</h4>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {article.relatedArticles.map((ra, i) => (
              <li key={i}>
                <Link
                  to={`/articles/${ra.slug}`}
                  className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors py-1.5"
                >
                  <i className="ri-article-line text-primary-400"></i>
                  {ra.title}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/" className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors py-1.5">
                <i className="ri-home-4-line"></i>
                Back to Kakobuy Spreadsheet Home
              </Link>
            </li>
          </ul>
        </div>

        <div className="mt-10 text-center p-8 md:p-10 rounded-2xl bg-foreground-900">
          <h3 className="text-xl md:text-2xl font-heading font-bold text-background-50">
            Continue Exploring Kakobuy Spreadsheet
          </h3>
          <p className="mt-3 text-sm md:text-base text-background-300 max-w-md mx-auto">
            Ready to find your next great product? Browse the full spreadsheet directory now.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-file-list-3-line"></i>
              Open Spreadsheet
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-background-300 text-background-200 font-semibold text-sm hover:border-background-100 hover:text-background-50 transition-all whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}